import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useClient } from "@/context/ClientContext";
import {
  liStatus, liConnectUrl, liSelectOrg, liDisconnect, liGenerate, liOptimize,
  liCompose, liPublish, liPosts, liAnalytics, liBestTimes, liTestConnect,
} from "@/lib/api";
import { toast } from "sonner";
import {
  LinkedinLogo, Sparkle, PaperPlaneTilt, CalendarPlus, Clock, ThumbsUp,
  ChatCircle, Eye, CursorClick, UsersThree, Plug, Warning, FilePdf,
} from "@phosphor-icons/react";

const STATUS_COLOR = {
  pending: "text-amber-300", approved: "text-sky-300", scheduled: "text-violet-300",
  published: "text-emerald-400", failed: "text-red-400",
};

function ConnectCard({ cid, s, refetch }) {
  if (!s) return null;
  if (!s.linkedin_configured && !s.connected) {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="li-not-configured-banner">
        <Warning size={20} className="text-amber-400 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-200">
          <div className="font-semibold">LinkedIn integration is not active yet.</div>
          <div className="mt-1 text-amber-200/80">
            The platform needs a LinkedIn Developer app (Community Management API). Add <code className="text-amber-100">LINKEDIN_CLIENT_ID</code> and <code className="text-amber-100">LINKEDIN_CLIENT_SECRET</code> to deployment secrets — drafting, AI generation, scheduling and approvals below work right now; connecting &amp; publishing activate automatically.
          </div>
          <Button data-testid="li-test-connect-btn" size="sm" onClick={async () => { await liTestConnect(cid); refetch(); }}
            className="mt-3 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30 h-8 text-xs">
            <Plug size={13} className="mr-1.5" /> Enable Test Mode — try the full workflow with simulated publishing
          </Button>
        </div>
      </div>
    );
  }
  if (!s.connected) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 flex flex-wrap items-center gap-4" data-testid="li-connect-card">
        <div className="w-11 h-11 rounded-xl bg-[#0A66C2]/20 flex items-center justify-center"><LinkedinLogo size={24} weight="fill" className="text-[#0A66C2]" /></div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-semibold">Connect your LinkedIn Company Page</div>
          <div className="text-xs text-[#9a9a9a] mt-0.5">You'll need admin access to the page. Multiple pages are supported for agencies.</div>
        </div>
        <Button data-testid="li-connect-btn" className="bg-[#0A66C2] hover:bg-[#0a7ae8] text-white font-semibold"
          onClick={() => { window.location.href = liConnectUrl(cid); }}>
          <Plug size={16} className="mr-1.5" /> Connect LinkedIn
        </Button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex flex-wrap items-center gap-3" data-testid="li-connected-card">
      <Badge className="bg-[#0A66C2] text-white border-0"><LinkedinLogo size={13} weight="fill" className="mr-1" /> Connected</Badge>
      {s.test_mode && <Badge data-testid="li-test-mode-badge" className="bg-amber-500/20 text-amber-300 border border-amber-500/30">TEST MODE — publishing simulated</Badge>}
      <Select value={s.selected_org || ""} onValueChange={async (v) => { await liSelectOrg(cid, v); toast.success("Company Page selected"); refetch(); }}>
        <SelectTrigger className="h-9 w-72 bg-white/5 border-white/10" data-testid="li-org-select"><SelectValue placeholder="Choose a Company Page" /></SelectTrigger>
        <SelectContent className="bg-[#16161c] border-white/10 text-white">
          {(s.organizations || []).map((o) => <SelectItem key={o.urn} value={o.urn}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" className="ml-auto border-white/15 h-8 text-xs" data-testid="li-disconnect-btn"
        onClick={async () => { await liDisconnect(cid); toast.message("LinkedIn disconnected"); refetch(); }}>Disconnect</Button>
    </div>
  );
}

function Composer({ cid, connected }) {
  const qc = useQueryClient();
  const [topic, setTopic] = useState("");
  const [text, setText] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [docB64, setDocB64] = useState(null);
  const [docName, setDocName] = useState("");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState("");

  const gen = async () => {
    if (!topic.trim()) return toast.error("Give the AI a topic first");
    setBusy("gen");
    try {
      const r = await liGenerate({ client_id: cid, topic });
      setText(r.commentary); setHashtags(r.hashtags || "");
      toast.success("LinkedIn post drafted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(""); }
  };

  const opt = async () => {
    if (!text.trim()) return;
    setBusy("opt");
    try { const r = await liOptimize(cid, text); setText(r.optimized); toast.success("Post optimized for engagement"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Optimization failed"); }
    finally { setBusy(""); }
  };

  const onDoc = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) return toast.error("PDF must be under 8MB");
    const reader = new FileReader();
    reader.onload = () => { setDocB64(String(reader.result).split(",")[1]); setDocName(f.name); };
    reader.readAsDataURL(f);
  };

  const save = async (autoApprove) => {
    if (!text.trim()) return toast.error("Write or generate a post first");
    setBusy("save");
    try {
      await liCompose({ client_id: cid, commentary: text, hashtags, image_url: imageUrl,
        document_b64: docB64, document_name: docName,
        scheduled_at: when ? new Date(when).toISOString() : null, auto_approve: autoApprove });
      toast.success(autoApprove ? (when ? "Scheduled — Auto Pilot will publish it" : "Approved and ready to publish") : "Draft sent to the Approval Queue");
      setText(""); setHashtags(""); setTopic(""); setImageUrl(""); setDocB64(null); setDocName(""); setWhen("");
      qc.invalidateQueries({ queryKey: ["li-posts", cid] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save post"); }
    finally { setBusy(""); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="li-composer">
      <div className="lg:col-span-2 space-y-3">
        <div className="flex gap-2">
          <Input placeholder="Topic — e.g. 'Why local roasters beat big coffee chains'" value={topic}
            onChange={(e) => setTopic(e.target.value)} data-testid="li-topic-input" />
          <Button onClick={gen} disabled={busy === "gen"} data-testid="li-generate-btn"
            className="bg-[#FF5E00] hover:bg-[#ff7a2b] text-black font-semibold shrink-0">
            <Sparkle size={16} className="mr-1.5" /> {busy === "gen" ? "Writing…" : "AI Draft"}
          </Button>
        </div>
        <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} data-testid="li-post-text"
          placeholder="Your LinkedIn post — write it yourself or let the AI draft it…" className="bg-white/[0.03] border-white/10" />
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="#hashtags" value={hashtags} onChange={(e) => setHashtags(e.target.value)}
            className="w-64" data-testid="li-hashtags-input" />
          <Button size="sm" variant="outline" className="border-white/15" onClick={opt} disabled={busy === "opt" || !text}
            data-testid="li-optimize-btn"><Sparkle size={14} className="mr-1" /> {busy === "opt" ? "Optimizing…" : "AI Optimize"}</Button>
          <span className="text-[11px] text-[#9a9a9a] ml-auto">{text.length} / 3000</span>
        </div>
      </div>
      <div className="space-y-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Image URL (from AI Image Studio or web)</Label>
            <Input placeholder="https://…" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
              className="mt-1" data-testid="li-image-url" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Carousel / document (PDF)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="file" accept="application/pdf" onChange={onDoc} className="text-xs text-[#9a9a9a]" data-testid="li-doc-input" />
            </div>
            {docName && <div className="text-xs text-emerald-400 mt-1 flex items-center gap-1"><FilePdf size={13} /> {docName}</div>}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-[#9a9a9a]">Schedule (optional)</Label>
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)}
              className="mt-1" data-testid="li-schedule-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Button className="w-full bg-white/10 hover:bg-white/15" onClick={() => save(false)} disabled={busy === "save"}
            data-testid="li-save-draft-btn"><CalendarPlus size={16} className="mr-1.5" /> Send to Approval Queue</Button>
          <Button className="w-full bg-[#0A66C2] hover:bg-[#0a7ae8] text-white font-semibold" onClick={() => save(true)}
            disabled={busy === "save"} data-testid="li-approve-btn">
            <PaperPlaneTilt size={16} className="mr-1.5" /> {when ? "Approve & Schedule" : "Approve (publish-ready)"}
          </Button>
          {!connected && <p className="text-[11px] text-[#9a9a9a]">Posts wait safely in your queue — they publish once LinkedIn is connected.</p>}
        </div>
      </div>
    </div>
  );
}

function History({ cid, connected }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["li-posts", cid], queryFn: () => liPosts(cid), enabled: !!cid });
  const rows = q.data || [];
  const publish = async (id) => {
    try { const r = await liPublish(id); toast.success(`Published to ${r.page_name}`); qc.invalidateQueries({ queryKey: ["li-posts", cid] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };
  return (
    <div className="rounded-xl border border-white/10 overflow-x-auto" data-testid="li-history-table">
      <table className="w-full text-sm min-w-[760px]">
        <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-[#9a9a9a]">
          <tr><th className="p-3">Post</th><th className="p-3">Status</th><th className="p-3">Scheduled</th>
            <th className="p-3">Engagement</th><th className="p-3"></th></tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const m = (r.meta || {}).linkedin_metrics || {};
            const link = ((r.meta || {}).publish_result || {}).permalink;
            return (
              <tr key={r.id} className="border-t border-white/5">
                <td className="p-3 max-w-[340px]"><div className="truncate font-medium">{r.title}</div>
                  <div className="text-[11px] text-[#9a9a9a] truncate">{(r.body || "").slice(0, 90)}</div></td>
                <td className={`p-3 text-xs font-medium ${STATUS_COLOR[r.status] || ""}`}>{r.status}</td>
                <td className="p-3 text-xs text-[#9a9a9a]">{(r.scheduled_at || "").slice(0, 16).replace("T", " ") || "—"}</td>
                <td className="p-3 text-xs">
                  {r.status === "published"
                    ? <span className="flex items-center gap-3"><span className="flex items-center gap-1"><ThumbsUp size={13} />{m.likes ?? "–"}</span>
                      <span className="flex items-center gap-1"><ChatCircle size={13} />{m.comments ?? "–"}</span></span>
                    : <span className="text-[#9a9a9a]">—</span>}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {link && <a href={link} target="_blank" rel="noreferrer" className="text-xs text-[#0A66C2] hover:underline mr-3">View</a>}
                  {["approved", "scheduled"].includes(r.status) && connected && (
                    <Button size="sm" variant="outline" className="border-white/15 h-7 text-xs" data-testid={`li-publish-${r.id}`}
                      onClick={() => publish(r.id)}>Publish now</Button>
                  )}
                </td>
              </tr>
            );
          })}
          {!rows.length && <tr><td className="p-4 text-sm text-[#9a9a9a]" colSpan={5}>No LinkedIn posts yet — draft your first one in Compose.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Analytics({ cid }) {
  const q = useQuery({ queryKey: ["li-analytics", cid], queryFn: () => liAnalytics(cid), enabled: !!cid });
  const bt = useQuery({ queryKey: ["li-best-times", cid], queryFn: () => liBestTimes(cid), enabled: !!cid });
  const d = q.data || {};
  const t = d.totals || {};
  if (q.isLoading) return <div className="text-sm text-[#9a9a9a]">Loading analytics…</div>;
  return (
    <div className="space-y-4" data-testid="li-analytics-panel">
      {!d.connected && (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm text-[#9a9a9a]">
          Connect your LinkedIn Company Page to see live followers, impressions, clicks and engagement here.
        </div>
      )}
      {d.connected && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {[[UsersThree, "Followers", d.followers], [Eye, "Impressions", t.impressions],
            [CursorClick, "Clicks", t.clicks], [ThumbsUp, "Reactions", t.likes],
            [ChatCircle, "Comments", t.comments], [LinkedinLogo, "Engagement", `${t.engagement_rate ?? 0}%`]].map(([Icon, l, v]) => (
            <div key={l} className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#9a9a9a]"><Icon size={13} /> {l}</div>
              <div className="text-xl font-semibold mt-0.5">{v ?? 0}</div>
            </div>
          ))}
        </div>
      )}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="li-best-times">
        <div className="flex items-center gap-2 mb-3"><Clock size={16} className="text-[#FF5E00]" />
          <span className="font-semibold text-sm">Best times to post</span>
          <Badge variant="outline" className="border-white/15 text-[#9a9a9a] text-[10px]">
            {bt.data?.source === "your_page_data" ? "from your page's data" : "industry benchmarks (learns from your posts)"}
          </Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(bt.data?.times || []).map((x) => (
            <div key={x.day + x.time} className="rounded-lg border border-white/10 p-3">
              <div className="font-semibold text-sm">{x.day} · {x.time}</div>
              <div className="text-[11px] text-[#9a9a9a] mt-1">{x.reason}</div>
            </div>
          ))}
        </div>
      </div>
      {d.connected && (d.posts || []).length > 0 && (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-[#9a9a9a]">
              <tr><th className="p-3">Published post</th><th className="p-3">Date</th><th className="p-3">Reactions</th><th className="p-3">Comments</th></tr>
            </thead>
            <tbody>
              {d.posts.map((p) => (
                <tr key={p.id} className="border-t border-white/5">
                  <td className="p-3 max-w-[360px] truncate">{p.title}</td>
                  <td className="p-3 text-xs text-[#9a9a9a]">{(p.published_at || "").slice(0, 10)}</td>
                  <td className="p-3">{p.likes}</td><td className="p-3">{p.comments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function LinkedInCenterPage() {
  const { activeId, activeClient } = useClient();
  const sq = useQuery({ queryKey: ["li-status", activeId], queryFn: () => liStatus(activeId), enabled: !!activeId });
  const s = sq.data;
  return (
    <PageContainer>
      <PageHeader eyebrow="Marketing Channels" title="LinkedIn Center"
        subtitle={`AI-powered LinkedIn marketing for ${activeClient?.name || "your workspace"} — company page publishing, scheduling, approvals and analytics.`} />
      <div className="space-y-5">
        <ConnectCard cid={activeId} s={s} refetch={sq.refetch} />
        <Tabs defaultValue="compose">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="compose" data-testid="li-tab-compose">Compose</TabsTrigger>
            <TabsTrigger value="history" data-testid="li-tab-history">Posts & History</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="li-tab-analytics">Analytics</TabsTrigger>
          </TabsList>
          <TabsContent value="compose" className="mt-4"><Composer cid={activeId} connected={!!s?.connected} /></TabsContent>
          <TabsContent value="history" className="mt-4"><History cid={activeId} connected={!!s?.connected} /></TabsContent>
          <TabsContent value="analytics" className="mt-4"><Analytics cid={activeId} /></TabsContent>
        </Tabs>
      </div>
    </PageContainer>
  );
}
